import React, { useCallback, useLayoutEffect, useRef } from "react";

import "./floating-bar.css";
import { useAppContext } from "../../hooks/useAppContext";
import { useMutationObserver } from "../../hooks/useMutationObserver";
import { FloatingBarProvider, useFloatingBar } from "./use-floating-bar";

function assignRef<T>(ref: React.Ref<T> | undefined, value: T) {
	if (!ref) return;

	if (typeof ref === "function") {
		ref(value);
		return;
	}

	(ref as { current: T }).current = value;
}

function FloatingBarContent({
	className,
	style,
	ref,
	maxAllowedWidth,
	children,
}: {
	className?: string;
	style?: React.CSSProperties;
	ref?: React.Ref<HTMLDivElement>;
	maxAllowedWidth?: number;
	children?: React.ReactNode;
}) {
	const floatingBarRef = useRef<HTMLDivElement | null>(null);
	const setRef = useCallback(
		(node: HTMLDivElement | null) => {
			floatingBarRef.current = node;
			assignRef(ref, node);
		},
		[ref],
	);
	const [, { setShrinkLevel }] = useFloatingBar();
	const { logger } = useAppContext();
	const didLogRef = useRef(false);
	const isDisabled = maxAllowedWidth === undefined;

	const calculateShrinkLevel = useCallback(
		(availableWidth: number) => {
			if (!floatingBarRef.current) return;

			const levelData: {
				width: number;
				isHidden: boolean;
				hideLevel: number;
				showLevel: number;
			}[] = [];

			function shouldBeHidden(currentLevel: number, hideLevel = Infinity, showLevel = 0) {
				let shouldHide = false;
				if (currentLevel >= hideLevel) shouldHide = true;
				if (currentLevel < showLevel) shouldHide = true;
				return shouldHide;
			}

			floatingBarRef.current
				.querySelectorAll<HTMLElement>(".floating-bar-group")
				.forEach((el) => {
					const hideLevelStr = el.dataset["hideLevel"];
					const hideLevel = hideLevelStr ? parseInt(hideLevelStr, 10) : Infinity;
					const showLevelStr = el.dataset["showLevel"];
					const showLevel = showLevelStr ? parseInt(showLevelStr, 10) : 0;
					const elWidth = el.getBoundingClientRect().width;
					const isHidden = el.className.includes("floating-bar-group--hidden");
					levelData.push({ width: elWidth, isHidden, hideLevel, showLevel });
				});

			const measuredWidth = floatingBarRef.current.getBoundingClientRect().width ?? 0;
			const floatingBarStyle = window.getComputedStyle(floatingBarRef.current);
			const gap = parseFloat(floatingBarStyle.columnGap || floatingBarStyle.gap || "0") || 0;
			let allItemsWidth = measuredWidth;
			levelData.forEach((item) => {
				if (item.isHidden) allItemsWidth += item.width + gap;
			});

			const levels = levelData
				.flatMap((item) => [item.hideLevel, item.showLevel])
				.filter((level) => level !== Infinity && level !== 0)
				.filter((value, index, self) => self.indexOf(value) === index)
				.sort();
			levels.unshift(0);

			let fitLevel = 0;
			levels.some((level) => {
				let newWidth = allItemsWidth;
				levelData.forEach((item) => {
					if (shouldBeHidden(level, item.hideLevel, item.showLevel)) {
						newWidth -= item.width + gap;
					}
				});

				if (newWidth <= availableWidth) {
					fitLevel = level;
					setShrinkLevel(level);
					return true;
				}

				return false;
			});

			if (!fitLevel && !didLogRef.current) {
				logger.warn(
					`Content of FloatingBar does not fit within width: ${availableWidth}px).\nConsider adding a/more FloatingBarGroup with a hideLevel.`,
				);
				didLogRef.current = true;
			}
		},
		[logger, setShrinkLevel],
	);

	useLayoutEffect(() => {
		if (maxAllowedWidth === undefined) {
			setShrinkLevel(0);
			return;
		}

		calculateShrinkLevel(maxAllowedWidth);
	}, [calculateShrinkLevel, maxAllowedWidth, setShrinkLevel]);

	useMutationObserver(
		floatingBarRef,
		() => {
			if (maxAllowedWidth === undefined) return;
			calculateShrinkLevel(maxAllowedWidth);
		},
		{
			childList: true,
			attributes: true,
			subtree: true,
		},
		isDisabled,
	);

	return (
		<div ref={setRef} className={`floating-bar ${className || ""}`} style={style}>
			{children}
		</div>
	);
}

/**
 * Floating bar without a provider — renders the bar and computes shrinkLevel via a parent FloatingBarProvider.
 * Use this when the provider is already in the tree (e.g. provided by TopMenu.Root).
 */
export function FloatingBarBar({
	children,
	className,
	maxAllowedWidth,
	ref,
	style,
}: {
	children?: React.ReactNode;
	className?: string;
	maxAllowedWidth?: number;
	ref?: React.Ref<HTMLDivElement>;
	style?: React.CSSProperties;
}) {
	return (
		<FloatingBarContent
			className={className}
			maxAllowedWidth={maxAllowedWidth}
			ref={ref}
			style={style}
		>
			{children}
		</FloatingBarContent>
	);
}

/**
 * Floating bar shown on preview area
 * Can be used for View menu, Top menu and Navigation menu
 */
export function FloatingBar({
	children,
	className,
	maxAllowedWidth,
	ref,
	style,
}: {
	children?: React.ReactNode;
	className?: string;
	maxAllowedWidth?: number;
	ref?: React.Ref<HTMLDivElement>;
	style?: React.CSSProperties;
}) {
	return (
		<FloatingBarProvider>
			<FloatingBarContent
				className={className}
				maxAllowedWidth={maxAllowedWidth}
				ref={ref}
				style={style}
			>
				{children}
			</FloatingBarContent>
		</FloatingBarProvider>
	);
}
